/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable prefer-destructuring */
import * as Sentry from '@sentry/browser';
import axios, { AxiosResponse, Method } from 'axios';
import { identity, pickBy } from 'lodash';
import qs from 'qs';
import { axiosRateLimit } from '../utils/axiosRateLimit';
import { assembleUrl } from './assembleUrl';
import { QueryParamsDictionary } from './QueryParamsDictionary';

// Helper type to get the return type of a controller. Eg GetTypeOfControllerFunctionResult<TypedRoutes, 'failureMode', 'POST', '/api/surveys/:surveyId/failureModes/copy'> is { id: string}
export type TypeOfControllerFunctionResult<
    TRoutes,
    TEntity extends keyof TRoutes,
    TMethod extends keyof TRoutes[TEntity],
    TRoute extends keyof TRoutes[TEntity][TMethod],
> = TRoutes[TEntity][TMethod][TRoute] extends {
    controller: (...x: any) => Promise<infer TReturn>;
}
    ? TReturn
    : never;

// Helper type to get the input type of a controller. Eg GetTypeOfControllerInputAllType<TypedRoutes, 'failureMode', 'POST', '/api/surveys/:surveyId/failureModes/copy'> is { routeParams: { surveyId: string }, body: { failureModeId: string, manifestationId: string } }
export type TypeOfControllerInputAllType<
    TRoutes,
    TEntity extends keyof TRoutes,
    TMethod extends keyof TRoutes[TEntity],
    TRoute extends keyof TRoutes[TEntity][TMethod],
> = TRoutes[TEntity][TMethod][TRoute] extends {
    controller: (req: any, res: any, input: infer TParams, user?: any) => Promise<unknown>;
}
    ? TParams
    : never;

export async function fetchJson(
    url: string,
    method: Method,
    body?: Record<string, unknown>,
): Promise<{ successful: boolean; status: number; json?: any; errorCode?: string | undefined }> {
    try {
        const response = await fetchJsonWithThrow(url, method, body);
        return { successful: true, status: response.status, json: response.json };
    } catch (error) {
        if (error instanceof FetchError) {
            if (error.statusCode === 401) {
                throw new FetchError(error.statusCode, error.errorCode, 'fetchJson: Not authenticated', url, method);
            } else if (error.statusCode === 429) {
                throw new FetchError(error.statusCode, error.errorCode, 'fetchJson: Too many requests', url, method);
            }
            return { successful: false, status: error.statusCode, errorCode: error.errorCode };
        }
        return { successful: false, status: 0, errorCode: error.message };
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function fetchGet<Result = any>(
    url: string,
    queryParams?: QueryParamsDictionary,
    allowedErrorCodes?: string[],
    allowedHttpErrorCodes?: number[],
): Promise<{ status: number; json?: Result; errorCode?: string }> {
    const completeUrl = assembleUrl(url, queryParams);
    return await fetchJsonWithThrow(completeUrl, 'GET', undefined, allowedErrorCodes, allowedHttpErrorCodes);
}

export async function fetchDelete(url: string, queryParams?: QueryParamsDictionary, allowedErrorCodes?: string[], allowedHttpErrorCodes?: number[]) {
    const completeUrl = assembleUrl(url, queryParams);
    return await fetchJsonWithThrow(completeUrl, 'DELETE', undefined, allowedErrorCodes, allowedHttpErrorCodes);
}

export async function fetchPost(
    url: string,
    queryParams?: QueryParamsDictionary,
    body?: Record<string, unknown>,
    allowedErrorCodes?: string[],
    allowedHttpErrorCodes?: number[],
) {
    const completeUrl = assembleUrl(url, queryParams);
    return await fetchJsonWithThrow(completeUrl, 'POST', body, allowedErrorCodes, allowedHttpErrorCodes);
}

const rateLimitedAxios = axiosRateLimit(axios.create(), { maxRequests: 150, perMilliseconds: 1000 * 10 });

export async function fetchJsonWithThrow(
    url: string,
    method: Method,
    body?: Record<string, unknown>,
    allowedErrorCodes?: string[],
    allowedHttpErrorCodes?: number[],
    queryParams?: QueryParamsDictionary,
): Promise<{ status: number; json?: any; errorCode?: string }> {
    const zxyToken = sessionStorage.getItem('zxy-token');
    let response: AxiosResponse;
    let status = 0;
    let code = 'DEFAULT';

    try {
        response = await rateLimitedAxios(url, {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'zxy-token': zxyToken,
            },
            withCredentials: true,
            data: body,
            params: queryParams,
            paramsSerializer: {
                serialize: (params) => {
                    // https://stackoverflow.com/questions/30812765
                    const nonEmptyParams = pickBy(params, identity);
                    return qs.stringify(nonEmptyParams, { arrayFormat: 'brackets' });
                },
            },
            timeout: 1000 * 60 * 30,
        });
    } catch (error) {
        const errorResponse = error.response;

        let message = '';
        if (errorResponse) {
            status = errorResponse.status;
            code = errorResponse.data.code;

            if (allowedErrorCodes && allowedErrorCodes.includes(code)) {
                return { status, errorCode: code };
            }
            if (allowedHttpErrorCodes && allowedHttpErrorCodes.includes(status)) {
                return { status, errorCode: code };
            }

            const serverHeader = errorResponse.headers['server'];

            if (serverHeader === 'awselb/2.0') {
                Sentry.captureException(error, {
                    extra: {
                        url,
                        queryParams,
                        method,
                        body,
                        responseHeaders: JSON.stringify(errorResponse.headers),
                        status,
                        data: errorResponse.data,
                        statusText: errorResponse.statusText,
                        response: JSON.stringify(errorResponse),
                    },
                });
                throw new FetchError(status, code, message, url, method);
            } else if (status === 401) {
                throw new FetchError(status, code, message, url, method);
            } else if (error.statusCode === 429) {
                throw new FetchError(status, code, message, url, method);
            }

            message = `message='${errorResponse.statusText}' send zxytoken='${zxyToken}'`;
        } else {
            Sentry.captureException(error);
            if (axios.isAxiosError(error)) {
                code = error.code ?? 'unknown';
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                message = `Axios error message="${error.message}",name="${error.name}",url="${url}",queryParams="${queryParams}",stack="${error.stack},isCancel="${axios.isCancel(error)}",code="${error.code}`;
            } else {
                message = JSON.stringify(error);
            }
        }
        throw new FetchError(status, code, message, url, method, errorResponse?.data);
    }

    if (response.headers['content-type'] && response.headers['content-type'].indexOf('text/html') !== -1) {
        throw Error(`Received HTML, does this route exist? (${url})`);
    }
    if (response.headers['content-length'] && response.headers['content-length'] === '0') {
        console.log('content length is 0', response);
        return { status };
    }

    status = response.status;
    const json = response.data;

    return { status, json };
}

export class FetchError extends Error {
    public statusCode: number;
    public errorCode?: string | undefined;
    public innerError?: Error | string | undefined;

    constructor(
        statusCode: number,
        errorCode?: string,
        innerError?: Error | string,
        url?: string,
        method?: string,
        public responseData?: any,
    ) {
        super(`${statusCode} ${errorCode} ${innerError?.toString()} ${url} ${method}`);
        this.name = 'FetchError';
        if (innerError && typeof innerError === 'object' && 'stack' in innerError) {
            this.stack = innerError.stack;
        }
        this.statusCode = statusCode;
        this.errorCode = errorCode;
        this.innerError = innerError;
    }
}
